import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './TrainingVariantsTable.module.css';
import { useCoachOnboarding } from './coach/onboarding/CoachOnboardingProvider';
import { COACH_STEPS } from './coach/onboarding/coachOnboardingSteps';
import { HOST } from '../config/api';
import { getAuthToken } from '../lib/auth';
import { parseAiTrainingParameters } from '../features/coach/aiTrainingParameters';

const TrainingVariantsTable = ({ variants, userId, demoMode = false, onVariantUpdated = () => {}, showAiFields = true, aiParametersTemplate = '' }) => {
    const [copiedId, setCopiedId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editAgentDescription, setEditAgentDescription] = useState('');
    const [savingId, setSavingId] = useState(null);
    const [saveError, setSaveError] = useState('');
    const [saveNotice, setSaveNotice] = useState('');
    const copyTimeoutRef = useRef(null);
    const { setStepDone } = useCoachOnboarding();

    useEffect(() => () => {
        if (copyTimeoutRef.current) {
            clearTimeout(copyTimeoutRef.current);
        }
    }, []);

    const shareLinkFor = (shareCode) => `${window.location.origin}/s/${shareCode}`;

    const startEditing = (variant) => {
        setEditingId(variant.id);
        setEditName(variant.name || '');
        setEditDescription(variant.description || '');
        setEditAgentDescription(variant.agentDescription || aiParametersTemplate || '');
        setSaveError('');
        setSaveNotice('');

        const result = showAiFields ? parseAiTrainingParameters(variant.agentDescription || aiParametersTemplate || '') : null;
        setSaveNotice(result?.notice || '');
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditName('');
        setEditDescription('');
        setEditAgentDescription('');
        setSaveError('');
        setSaveNotice('');
    };

    const handleShare = async (shareCode) => {
        setStepDone(COACH_STEPS.PLAN_SHARED);
        try {
            await navigator.clipboard.writeText(shareLinkFor(shareCode));
            setCopiedId(shareCode);
            if (copyTimeoutRef.current) {
                clearTimeout(copyTimeoutRef.current);
            }
            copyTimeoutRef.current = setTimeout(() => setCopiedId(null), 1500);
        } catch (error) {
            console.error('Copy failed', error);
            prompt('Copy this link:', shareLinkFor(shareCode));
        }
    };

    const handleSaveChanges = async (variant) => {
        if (!editName.trim()) {
            setSaveError('Training plan name is required.');
            return;
        }

        const agentParametersResult = showAiFields ? parseAiTrainingParameters(editAgentDescription) : null;
        if (agentParametersResult && !agentParametersResult.isValid) {
            setSaveNotice('');
            setSaveError(agentParametersResult.error);
            return;
        }

        const updatedVariant = {
            ...variant,
            name: editName.trim(),
            description: editDescription.trim(),
            agentDescription: agentParametersResult?.normalizedValue ?? variant.agentDescription,
        };

        if (demoMode) {
            onVariantUpdated(updatedVariant);
            cancelEditing();
            return;
        }

        try {
            setSavingId(variant.id);
            setSaveError('');

            const response = await fetch(`${HOST}/v1/training_plans`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify({
                    id: variant.id,
                    userId,
                    name: updatedVariant.name,
                    agentDescription: updatedVariant.agentDescription,
                    description: updatedVariant.description,
                    private: variant.private,
                }),
            });

            if (!response.ok) {
                throw new Error('Training plan changes could not be saved.');
            }

            onVariantUpdated(updatedVariant);
            cancelEditing();
        } catch (error) {
            setSaveError(error.message || 'Training plan changes could not be saved.');
        } finally {
            setSavingId(null);
        }
    };

    if (!variants || variants.length === 0) {
        return <p className={styles.emptyState}>No training plans yet.</p>;
    }

    return (
        <div className={styles.list}>
            {variants.map((variant) => {
                const isEditing = String(editingId) === String(variant.id);

                return (
                    <article key={variant.id} className={styles.planCard}>
                    <div className={styles.planMeta}>
                        <div className={styles.planTopRow}>
                            <span className={variant.private ? styles.badgePrivate : styles.badgePublic}>
                                {variant.private ? 'Private' : 'Public'}
                            </span>
                            <Link to={`/trainings?variant=${variant.id}`} className={styles.planLink}>
                                Open in trainings
                            </Link>
                        </div>

                        {isEditing ? (
                            <div className={styles.editForm}>
                                <label className={styles.editLabel} htmlFor={`variantName-${variant.id}`}>
                                    Training plan name
                                </label>
                                <input
                                    id={`variantName-${variant.id}`}
                                    className={styles.editInput}
                                    value={editName}
                                    onChange={(event) => setEditName(event.target.value)}
                                />

                                <label className={styles.editLabel} htmlFor={`variantDescription-${variant.id}`}>
                                    Description
                                </label>
                                <textarea
                                    id={`variantDescription-${variant.id}`}
                                    className={styles.editTextArea}
                                    value={editDescription}
                                    onChange={(event) => setEditDescription(event.target.value)}
                                    placeholder="Add a description for this training plan."
                                />

                                {showAiFields ? (
                                    <>
                                        <label className={styles.editLabel} htmlFor={`variantAgent-${variant.id}`}>
                                            AI training parameters
                                        </label>
                                        <textarea
                                            id={`variantAgent-${variant.id}`}
                                            className={styles.editTextArea}
                                            value={editAgentDescription}
                                            onChange={(event) => {
                                                setEditAgentDescription(event.target.value);
                                                setSaveError('');
                                                const result = parseAiTrainingParameters(event.target.value);
                                                setSaveNotice(result.notice || '');
                                            }}
                                            placeholder={aiParametersTemplate || 'Paste the JSON training parameters for this plan.'}
                                        />
                                    </>
                                ) : null}

                                {saveNotice && <p className={styles.editNotice}>{saveNotice}</p>}
                                {saveError && <p className={styles.editError}>{saveError}</p>}

                                <button
                                    type="button"
                                    className={styles.primaryButton}
                                    onClick={() => handleSaveChanges(variant)}
                                    disabled={savingId === variant.id}
                                >
                                    {savingId === variant.id ? 'Saving…' : 'Save changes'}
                                </button>
                            </div>
                        ) : (
                            <>
                                <h4>{variant.name}</h4>
                                <p>{variant.description || 'No description yet.'}</p>
                            </>
                        )}
                    </div>

                    <div className={styles.planActions}>
                        <button
                            type="button"
                            className={styles.secondaryButton}
                            onClick={() => (isEditing ? cancelEditing() : startEditing(variant))}
                        >
                            {isEditing ? 'Cancel' : 'Edit'}
                        </button>
                        <button
                            type="button"
                            className={styles.secondaryButton}
                            onClick={() => handleShare(variant.shareCode)}
                        >
                            {copiedId === variant.shareCode ? 'Copied!' : 'Copy share link'}
                        </button>
                    </div>
                </article>
                );
            })}
        </div>
    );
};

export default TrainingVariantsTable;
